import React, { useEffect, useState, createContext, useContext } from 'react';
import {
  Box, Button, Paper, Typography, Stack, Divider,
  Alert, TextField, IconButton, InputAdornment, CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import Logo from '../assets/logo.svg';
const loginLogo: string | null = null;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export interface AuthUser { username: string; }

interface AuthCtx {
  user: AuthUser | null;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthCtx>({ user: null, logout: async () => {} });
export const useAuth = () => useContext(AuthContext);

// ---------------------------------------------------------------------------
// API helpers (cookie-based — credentials: 'include')
// ---------------------------------------------------------------------------

const apiFetch = (endpoint: string, options: RequestInit = {}) =>
  fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });

const apiLogin = async (username: string, password: string): Promise<AuthUser> => {
  const res = await apiFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Login failed (${res.status})`);
  }
  const data = await res.json().catch(() => ({}));
  return { username: data.user?.username ?? data.username ?? username };
};

const apiLogout = async (): Promise<void> => {
  await apiFetch('/auth/logout', { method: 'POST' }).catch(() => {});
};

const apiGetMe = async (): Promise<AuthUser | null> => {
  try {
    const res = await apiFetch('/auth/me');
    if (!res.ok) return null;
    const data = await res.json();
    return { username: data.user?.username ?? data.username };
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Splash screen
// ---------------------------------------------------------------------------

const SplashScreen: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const hold = setTimeout(() => setFading(true), 2200);
    const done = setTimeout(onDone, 2700);
    return () => { clearTimeout(hold); clearTimeout(done); };
  }, [onDone]);

  return (
    <>
      <style>{`
        @keyframes heartbeat {
          0%  { transform: scale(1);    }
          14% { transform: scale(1.12); }
          28% { transform: scale(1);    }
          42% { transform: scale(1.08); }
          56% { transform: scale(1);    }
          100%{ transform: scale(1);    }
        }
      `}</style>

      <Box sx={{
        position: 'fixed', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#ffffff', zIndex: 9999,
        opacity: fading ? 0 : 1, transition: 'opacity 0.5s ease-out',
      }}>
        {Logo ? (
          <Box component="img" src={Logo} alt="Logo" sx={{
            height: 120, width: 'auto', objectFit: 'contain',
            animation: 'heartbeat 1.4s ease-in-out infinite',
          }} />
        ) : (
          <Box sx={{
            width: 120, height: 120, borderRadius: '28px',
            background: 'linear-gradient(135deg, #F26522, #D4571A)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 32px rgba(242,101,34,0.25)',
            animation: 'heartbeat 1.4s ease-in-out infinite',
          }}>
            <Typography sx={{ fontSize: 56 }}>⚖️</Typography>
          </Box>
        )}
      </Box>
    </>
  );
};

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#f5f6fa',
    backgroundImage:
      'radial-gradient(ellipse at 20% 50%, rgba(242,101,34,0.07) 0%, transparent 60%),' +
      'radial-gradient(ellipse at 80% 20%, rgba(242,101,34,0.04) 0%, transparent 50%)',
    animation: 'fadeIn 0.45s ease-out',
    '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
  }}>
    <Paper elevation={0} sx={{
      p: 4, width: '100%', maxWidth: 440, borderRadius: 3,
      border: '1px solid', borderColor: 'divider',
      boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
    }}>
      {children}
    </Paper>
  </Box>
);

const CardHeader: React.FC = () => (
  <Box sx={{
    display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3,
    animation: 'slideDown 0.5s ease-out',
    '@keyframes slideDown': {
      from: { opacity: 0, transform: 'translateY(-12px)' },
      to:   { opacity: 1, transform: 'translateY(0)' },
    },
  }}>
    {loginLogo ? (
      <Box component="img" src={loginLogo} alt="Agentic Arbitrator"
        sx={{ height: 64, width: 'auto', objectFit: 'contain', mb: 1.5 }} />
    ) : (
      <Box sx={{
        width: 72, height: 72, borderRadius: '18px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        mb: 1.5,
      }}>
        <Typography sx={{ fontSize: 34 }}>⚖️</Typography>
      </Box>
    )}
    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#1a1a2e', letterSpacing: '-0.3px' }}>
      Agentic Arbitrator
    </Typography>
    <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
      Enterprise Supply Chain Intelligence
    </Typography>
  </Box>
);

const PasswordField: React.FC<{
  label: string; value: string;
  onChange: (v: string) => void; error?: string;
}> = ({ label, value, onChange, error }) => {
  const [show, setShow] = useState(false);
  return (
    <TextField fullWidth label={label} type={show ? 'text' : 'password'}
      value={value} onChange={(e) => onChange(e.target.value)}
      error={!!error} helperText={error} size="small"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => setShow(p => !p)} edge="end">
              {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// Login form
// ---------------------------------------------------------------------------

const LoginForm: React.FC<{ onSuccess: (u: AuthUser) => void }> = ({ onSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState<string | null>(null);
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async () => {
    setError(null);
    if (!username || !password) { setError('Please fill in all fields.'); return; }
    setLoading(true);
    try {
      const user = await apiLogin(username, password);
      onSuccess(user);
    } catch (err: any) {
      setError(err.message || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack component="form" spacing={2} onSubmit={(event) => { event.preventDefault(); void handleSubmit(); }}>
      {error && <Alert severity="error" sx={{ fontSize: '13px' }}>{error}</Alert>}

      <TextField fullWidth label="Username" size="small"
        value={username} onChange={(e) => setUsername(e.target.value)} />

      <PasswordField label="Password" value={password} onChange={setPassword} />

      <Button
        variant="contained"
        fullWidth
        type="submit"
        disabled={loading}
        sx={{
          backgroundColor: '#ff9800 !important',
          '&:hover': { backgroundColor: '#fb8c00 !important' },
        }}
      >
        {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Sign in'}
      </Button>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Auth gate
// ---------------------------------------------------------------------------

const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showSplash, setShowSplash] = useState(true);
  const [user, setUser]             = useState<AuthUser | null>(null);
  const [checking, setChecking]     = useState(true);

  // On mount, check if a valid session cookie already exists
  useEffect(() => {
    apiGetMe().then((u) => {
      setUser(u);
      setChecking(false);
    });
  }, []);

  const logout = async () => {
    await apiLogout();
    setUser(null);
  };

  if (checking) {
    return (
      <Box sx={{
        position: 'fixed', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#ffffff',
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (user) {
    return (
      <AuthContext.Provider value={{ user, logout }}>
        {children}
      </AuthContext.Provider>
    );
  }

  if (showSplash) return <SplashScreen onDone={() => setShowSplash(false)} />;

  return (
    <PageWrapper>
      <CardHeader />
      <Divider sx={{ mb: 3 }} />

      <LoginForm onSuccess={setUser} />

      <Typography variant="caption" color="text.disabled"
        sx={{ display: 'block', textAlign: 'center', mt: 2.5 }}>
        By signing in you agree to your organization's acceptable use policy
      </Typography>
    </PageWrapper>
  );
};

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AuthGate>{children}</AuthGate>
);

export const useCurrentUser = () => useAuth().user;
export const useLogout      = () => useAuth().logout;
